<template>
  <div
    v-if="factory && factory.status !== 0"
    class="factory-wrapper"
  >
    <div class="card">
      <div class="header">
        <img
          src="@/assets/factory/tab.svg"
          alt=""
        >
        <div class="title">
          Konw Your Supplier
        </div>
        <Button
          class="follow"
          :class="{
            'followed': isFollowed
          }"
          @click="onFollowClick(factory.sellerOpenId)"
          :loading="following"
        >
          <template v-if="isFollowed">
            <Icon
              class="icon"
              name="right-l"
            />
            Followed
          </template>
          <template v-else>
            Follow Factory
          </template>
        </Button>
      </div>
      <div class="body">
        <img
          class="mask"
          src="@/assets/factory/h5-mask.png"
          alt=""
        >
        <div class="factory-info">
          <img
            class="logo"
            src="@/assets/factory/default-logo.svg"
            alt=""
          >
          <div
            @click="goSupplier"
            class="info"
          >
            <div class="row-1">
              {{ factory.supplierName }}
              <Icon
                class="icon"
                name="arrow-right-l-2"
              />
            </div>
            <div class="row-2">
              <img
                src="@/assets/illus/illus-supplier-trusted.svg"
                height="14px"
                alt=""
              >
              <span class="text">Credit Rating</span>
              <img
                src="@/assets/colorIcon/star.svg"
                alt=""
              >
              <span class="rate">{{ factory.supplierRatingVo.compositeService.toFixed(1) }}<span class="total"> / 5</span></span>
            </div>
            <div class="row-3">
              <div
                v-for="(tag, index) in factory.tagNameList.slice(0, 2)"
                :key="index"
                class="item"
              >
                {{ tag }}
              </div>
            </div>
          </div>
        </div>
        <div class="summary">
          <div class="column">
            <div class="value-1">
              {{ factory.supplierRatingVo ? `${Math.ceil(factory.supplierRatingVo.repeatPurchasePercent * 100)}%` : '-' }}
            </div>
            <div class="value-2">
              last 90 days
            </div>
            <div class="value-3">
              Reorder rate
            </div>
          </div>
          <div class="column">
            <div class="value-1">
              {{ factory.customerCount ? formatSalesVolume(factory.customerCount) : '-' }}
            </div>
            <div class="value-2">
              total
            </div>
            <div class="value-3">
              Store followers
            </div>
          </div>
          <div class="column">
            <div class="value-1">
              {{ factory.soldOut ? formatAmount(factory.soldOut) : '-' }}
            </div>
            <div class="value-2">
              last 30 days
            </div>
            <div class="value-3">
              Number of paid orders
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { formatSalesVolume, formatAmount, formatName, buriedPointUtil } from '@/utils/utils'
import { useFactory } from '@/components/SupplierHeader/useFactory'
import { useRouter } from 'vue-router'

const props = defineProps({
  factoryId: {
    type: String,
    required: true
  }
})

const router = useRouter()

const factoryId = computed(() => props.factoryId)

const { factory, isFollowed, toggleFollow } = useFactory(factoryId)

const following = ref(false)

const onFollowClick = async () => {
  following.value = true
  await toggleFollow('Detail')
  following.value = false
}

const goSupplier = () => {
  buriedPointUtil({ eventId: 'HPD212', others: { location: 'Detail' } })
  const { href } = router.resolve({
    name: 'factory',
    params: {
      name: formatName(factory.value.supplierName),
      id: factoryId.value
    }
  })
  window.open(href, '_blank')
}
</script>

<style lang="less" scoped>
.factory-wrapper {
  padding: 12px;
  margin-top: 8px;
  background-color: white;

  .card {
    position: relative;
    background: linear-gradient(to right, #4452A1 20%, #191E3B 100%);
    border-radius: 8px 8px 10px 10px;
    display: flex;
    flex-direction: column;

    .header {
      position: relative;
      height: 40px;
      img {
        position: relative;
        top: 3.5px;
        width: 60%;
        height: 100%;
      }
      .title {
        position: absolute;
        top: 16px;
        left: 12px;
        color: #232323;
        font-size: 16px;
        font-family: Young Serif;
        font-weight: 400;
      }
      .follow {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 114px;
        height: 28px;
        border-radius: 16px;
        background-color: #b0c7f5;
        color: #232323;
        font-size: 12px;
        font-family: mon-700;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;

        &.followed {
          background-color: #ebebeb;
          color: #232323;
          width: 98px;
        }
        .icon {
          font-size: 16px;
        }
      }

    }
    .body {
      position: relative;
      flex: 1;
      background-color: #F4F7FF;
      border-radius: 0 8px 8px 8px;
      padding: 12px;
      overflow: hidden;

      .mask {
        position: absolute;
        top: -20px;
        right: 0;
        width: 82px;
        z-index: 1;
      }

      .factory-info {
        display: flex;
        gap: 12px;
        .logo {
          width: 60px;
          height: 60px;
        }
        .info {
          .row-1 {
            display: flex;
            align-items: center;
            font-size: 13px;
            font-family: Young Serif;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            .icon {
              margin-left: 4px;
            }
          }
          .row-2 {
            display: flex;
            align-items: center;
            margin-top: 4px;
            .text {
              margin-left: 4px;
              margin-right: 2px;
              color: #232323;
              font-size: 11px;
              font-family: mon-500;
            }
            .rate {
              color: #232323;
              font-size: 11px;
              font-family: mon-600;
              margin-left: 2px;
              .total {
                color: #6e717a;
                font-family: mon-500;
              }
            }
          }
          .row-3 {
            margin-top: 4px;
            display: flex;
            gap: 4px;
            .item {
              border-radius: 2px;
              border: 1px solid #dfdfdf;
              height: 18px;
              padding: 0 6px;
              display: flex;
              justify-content: center;
              align-items: center;
              color: #232323;
              font-size: 10px;
              font-family: mon-600;
            }
          }
        }
      }

      .summary {
        position: relative;
        z-index: 2;
        margin-top: 16px;
        background-color: white;
        border-radius: 8px;
        height: 86px;
        padding: 12px 17px;
        display: flex;
        gap: 20px;
        .column {
          min-width: 62px;
          .value-1 {
            color: #232323;
            font-size: 20px;
            font-family: mon-700;
            line-height: 28px;
          }
          .value-2 {
            color: #6e717a;
            font-size: 10px;
            font-family: mon-500;
            line-height: 14px;
          }
          .value-3 {
            color: #5c5c5c;
            font-size: 11px;
            font-family: mon-500;
            margin-top: 8px;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
          }
        }
      }
    }
  }
}
</style>
